/**
 * The password change an administrator required.
 *
 * Rendered by `RequireAuth` in place of the whole shell while
 * `user.mustChangePassword` is set — not a route, so no link or bookmark can
 * get round it. That is presentation only: the server answers every other call
 * with 403 `password_change_required` until the change is made, which is what
 * actually holds the user here.
 *
 * Changing the password ends every session, this one too, as it does from the
 * profile. Signing out locally then lets `RequireAuth` send the user to the
 * sign-in screen, remembering where they were going.
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import KeyIcon from '@mui/icons-material/Key';
import { ErrorState } from '../components/ErrorState';
import { StandaloneScreen } from '../components/StandaloneScreen';
import { HealthBanner } from '../layout/HealthBanner';
import { useAuth } from '../auth/useAuth';
import { useSubmit } from '../hooks/useSubmit';
import { changeOwnPassword } from '../api/auth';

export default function ForcedPasswordChangePage() {
  const { t } = useTranslation('auth');
  const { user, signOut } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeated, setRepeated] = useState('');

  const change = useSubmit(async () => {
    await changeOwnPassword({ currentPassword, newPassword });
    // Every session is gone, this one included.
    await signOut();
    return true;
  });

  const mismatch = repeated !== '' && newPassword !== repeated;
  const incomplete = currentPassword === '' || newPassword === '' || newPassword !== repeated;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!incomplete) void change.submit();
  }

  return (
    <StandaloneScreen>
      <Stack spacing={3}>
        <HealthBanner />

        <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <KeyIcon color="warning" sx={{ fontSize: 40 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('forcedChange.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('forcedChange.description')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label={t('profile.password.current')}
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              autoFocus
              fullWidth
              disabled={change.pending}
            />
            <TextField
              label={t('profile.password.new')}
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              fullWidth
              disabled={change.pending}
            />
            <TextField
              label={t('profile.password.repeat')}
              type="password"
              value={repeated}
              onChange={(event) => setRepeated(event.target.value)}
              autoComplete="new-password"
              error={mismatch}
              helperText={mismatch ? t('profile.password.mismatch') : undefined}
              fullWidth
              disabled={change.pending}
            />

            <Alert severity="info">{t('forcedChange.warning')}</Alert>
            {change.error && <ErrorState error={change.error} severity="warning" />}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={change.pending || incomplete}
              startIcon={change.pending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {change.pending ? t('forcedChange.submitting') : t('forcedChange.submit')}
            </Button>
            <Button variant="text" onClick={() => void signOut()} disabled={change.pending}>
              {t('forcedChange.signOut')}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </StandaloneScreen>
  );
}
